import type { PersonId } from '@shared/brand';

/**
 * El expediente personal: lo que RR.HH. recogió en el formulario de la familia
 * Knotion y que no cabe en la ficha de trabajo.
 *
 * **Colección aparte, y no campos de `people`.** Un líder puede leer la ficha de
 * quien está en su área —lo necesita para el calendario y las aprobaciones—, y
 * añadir aquí el estado civil, las alergias o el tipo de sangre le habría dado de
 * paso el expediente de su equipo entero sin que nadie lo decidiera. Es el mismo
 * motivo por el que el sueldo vive en `salaries` y el contacto de emergencia en
 * `emergencyContacts`: quien lee la ficha no es quien lee esto.
 *
 * **Todo es opcional.** El formulario lo contestaron 326 de 337 personas y no
 * todas contestaron todo —el tipo de sangre lo trae el 81 %, las alergias el
 * 75 %—, así que un campo vacío es lo normal y no un error que reparar. La ficha
 * lo enseña como «Sin registrar», que es información: dice a quién falta
 * preguntarle.
 */
export interface PersonalRecord {
  /** Mismo valor que el id del documento. */
  readonly personId: PersonId;
  readonly maritalStatus: MaritalStatus | null;
  /** Si tiene un crédito INFONAVIT vivo. Lo pide nómina para el descuento. */
  readonly hasInfonavitCredit: boolean | null;
  readonly educationLevel: EducationLevel | null;
  readonly bloodType: BloodType | null;
  /**
   * Cuántos hijos. `null` es «no contestó» y `0` es «ninguno»: son cosas
   * distintas y el formulario las distingue.
   */
  readonly children: number | null;
  /**
   * Texto libre, y tiene que serlo.
   *
   * Las respuestas reales van de «penicilina» a «alergias en vías respiratorias
   * al polvo, polen y cambios drásticos». Un catálogo habría obligado a tirar la
   * mitad de lo que la gente escribió, que es justo lo que hace falta leer si
   * alguien se pone malo.
   */
  readonly allergies: string;
  /** El personal, distinto del corporativo. Por si deja de tener el de trabajo. */
  readonly personalEmail: string;
  readonly personalPhone: string;
  /**
   * Cuándo aceptó el aviso de privacidad, si lo hizo.
   *
   * Se guarda porque es el consentimiento con el que se recogió todo lo demás.
   * Un expediente sin él es un expediente que nadie autorizó.
   */
  readonly privacyAcceptedAt: Date | null;
  readonly updatedAt: Date;
  /** Quién lo tocó por última vez. Un expediente sin autor no se audita. */
  readonly updatedByPersonId: PersonId | null;
}

// ---------------------------------------------------------------------------
// Los catálogos cerrados
//
// Se guarda el código y se muestra la etiqueta, como con los roles: el documento
// no debería llevar dentro la redacción de una pantalla, que cambia sola.
// ---------------------------------------------------------------------------

export const MARITAL_STATUSES = ['soltero', 'casado', 'union_libre', 'divorciado', 'viudo'] as const;
export type MaritalStatus = (typeof MARITAL_STATUSES)[number];

export const MARITAL_STATUS_LABEL: Readonly<Record<MaritalStatus, string>> = {
  soltero: 'Soltero(a)',
  casado: 'Casado(a)',
  union_libre: 'Unión libre',
  divorciado: 'Divorciado(a)',
  viudo: 'Viudo(a)',
};

export const EDUCATION_LEVELS = [
  'secundaria',
  'bachillerato',
  'tsu',
  'licenciatura',
  'postgrado',
  'doctorado',
] as const;
export type EducationLevel = (typeof EDUCATION_LEVELS)[number];

export const EDUCATION_LEVEL_LABEL: Readonly<Record<EducationLevel, string>> = {
  secundaria: 'Secundaria',
  bachillerato: 'Bachillerato / Preparatoria / Carrera técnica',
  tsu: 'Técnico Superior Universitario (TSU)',
  licenciatura: 'Licenciatura / Ingeniería',
  postgrado: 'Postgrado (Especialidad / Maestría)',
  doctorado: 'Doctorado',
};

/* Los ocho del ABO/Rh. El formulario sólo recogió seis; los otros dos existen. */
export const BLOOD_TYPES = ['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'] as const;
export type BloodType = (typeof BLOOD_TYPES)[number];

export const isMaritalStatus = (value: unknown): value is MaritalStatus =>
  typeof value === 'string' && (MARITAL_STATUSES as readonly string[]).includes(value);

export const isEducationLevel = (value: unknown): value is EducationLevel =>
  typeof value === 'string' && (EDUCATION_LEVELS as readonly string[]).includes(value);

export const isBloodType = (value: unknown): value is BloodType =>
  typeof value === 'string' && (BLOOD_TYPES as readonly string[]).includes(value);

/**
 * Cuánto del expediente está contestado, de 0 a 1.
 *
 * Para decir «faltan tres datos» en vez de obligar a recorrer la tarjeta
 * buscando huecos. Las alergias cuentan aunque digan «ninguna»: contestar que no
 * se tiene ninguna **es** una respuesta, y no distinguirlo dejaría a media
 * plantilla marcada como incompleta para siempre.
 */
export const recordCompleteness = (record: PersonalRecord | null): number => {
  if (record === null) return 0;
  const campos = [
    record.maritalStatus !== null,
    record.hasInfonavitCredit !== null,
    record.educationLevel !== null,
    record.bloodType !== null,
    record.children !== null,
    record.allergies.trim().length > 0,
    record.personalEmail.trim().length > 0,
    record.personalPhone.trim().length > 0,
  ];
  return campos.filter(Boolean).length / campos.length;
};
